import { useCallback } from 'react';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import { NewActivity } from 'types';

import {
  NotificationsApi,
  NotificationsResponse,
  SystemNotification,
} from 'api/NotificationsApi';

const NOTIFICATIONS_QUERY_KEY = 'notifications-query';

export const useNotificationsQuery = (
  queryOptions?: UseQueryOptions<NotificationsResponse>
) => {
  return useQuery<NotificationsResponse>(
    NOTIFICATIONS_QUERY_KEY,
    NotificationsApi.fetchNotifications,
    {
      retry: false,
      ...queryOptions,
    }
  );
};

export const useNotificationsQueryCache = () => {
  const queryClient = useQueryClient();

  const getNotificationsCache = useCallback(() => {
    return queryClient.getQueryData<NotificationsResponse>(
      NOTIFICATIONS_QUERY_KEY
    );
  }, [queryClient]);

  const setNotificationsLikes = useCallback(
    (likes: NewActivity) => {
      const notificationsCache = getNotificationsCache();

      if (notificationsCache) {
        queryClient.setQueryData<NotificationsResponse>(
          NOTIFICATIONS_QUERY_KEY,
          () => {
            return {
              ...notificationsCache,
              likes,
            };
          }
        );
      }
    },
    [getNotificationsCache, queryClient]
  );

  const setNotificationsMatches = useCallback(
    (matches: NewActivity) => {
      const notificationsCache = getNotificationsCache();

      if (notificationsCache) {
        queryClient.setQueryData<NotificationsResponse>(
          NOTIFICATIONS_QUERY_KEY,
          () => {
            return {
              ...notificationsCache,
              matches,
            };
          }
        );
      }
    },
    [getNotificationsCache, queryClient]
  );

  const setNotificationsList = useCallback(
    (notifications: SystemNotification[]) => {
      const notificationsCache = getNotificationsCache();

      if (notificationsCache) {
        queryClient.setQueryData<NotificationsResponse>(
          NOTIFICATIONS_QUERY_KEY,
          () => {
            return {
              ...notificationsCache,
              notifications,
            };
          }
        );
      }
    },
    [getNotificationsCache, queryClient]
  );

  return {
    getNotificationsCache,
    setNotificationsLikes,
    setNotificationsMatches,
    setNotificationsList,
  };
};
